import type {
  AnnouncementAdminSchema,
  AnnouncementScheduleStatusChoice,
} from '@gto/api-client/admin/react-query'

import { formatCampaignWindow } from './announcement-time-zone'

// C-1714 decided that overlapping campaigns are legitimate — the PokerArena
// season handover and the "STARTING SOON → LIVE NOW" stream banner both work by
// abutting windows — so nothing here warns or blocks. These helpers only make
// the three facts that decide an overlap (placement, window, priority) legible,
// which is the whole mitigation: two rows on the same placement both reading
// LIVE is the signal an operator is meant to notice.
//
// C-1852 narrows that further without contradicting it: `announcement-collision`
// flags the one overlap shape that actually costs an announcement — a priority
// tie on a surface that renders exactly one — still as a warning, never a block.

// The BE enum plus one FE-only verdict: `unknown` marks a row whose window this
// build cannot parse, so the BE has no such status to send or accept.
export type AnnouncementScheduleStatus = AnnouncementScheduleStatusChoice | 'unknown'

export const ANNOUNCEMENT_STATUS_LABEL: Record<AnnouncementScheduleStatus, string> = {
  paused: 'Paused',
  unknown: 'Unknown',
  ended: 'Ended',
  scheduled: 'Scheduled',
  live: 'Live',
}

/**
 * The statuses the list can be filtered by, in the order a campaign lives
 * through them. `unknown` is not here: it marks a row whose window this build
 * cannot parse, and the BE enum has no such value to ask for.
 *
 * The URL schema validates against this same list, so a pasted link with a
 * status the endpoint would reject with a 422 falls back to the unfiltered list.
 */
export const STATUS_FILTER_CHOICES = [
  'live',
  'scheduled',
  'paused',
  'ended',
] as const satisfies readonly AnnouncementScheduleStatusChoice[]

/**
 * Where the announcement sits relative to `now`, in precedence order: an
 * operator pause hides it whatever the schedule says, then a window we cannot
 * read at all, then a passed end date, then a start date still in the future.
 * Anything left is serving.
 *
 * `is_preview` is deliberately NOT folded in: a preview item is live for the
 * flag holders, so it gets its own badge rather than a status of its own.
 */
export const deriveScheduleStatus = (
  announcement: AnnouncementAdminSchema,
  now: Date,
): AnnouncementScheduleStatus => {
  if (announcement.is_paused) {
    return 'paused'
  }

  const nowMs = now.getTime()
  const startMs = Date.parse(announcement.start_date)
  const endMs =
    announcement.end_date === null || announcement.end_date === undefined
      ? null
      : Date.parse(announcement.end_date)

  // `Date.parse` answers NaN for a malformed date, and every comparison against
  // NaN is false — so an unreadable window would fall through both guards below
  // and render a green "Live" badge on a row nobody can vouch for. Say so
  // instead.
  if (Number.isNaN(startMs) || (endMs !== null && Number.isNaN(endMs))) {
    return 'unknown'
  }

  if (endMs !== null && endMs < nowMs) {
    return 'ended'
  }

  if (startMs > nowMs) {
    return 'scheduled'
  }

  return 'live'
}

export const NO_END_DATE_SYMBOL = '∞'

/** Stands in for a boundary the payload gave us but we cannot parse. */
export const UNREADABLE_DATE_SYMBOL = '—'

/**
 * `"2 Jul 2026, 14:30 → ∞"` from epoch ms, so an unsaved form can be rendered
 * beside stored records — `Infinity` is the open-ended end, `NaN` a boundary
 * nobody can read.
 */
export const formatWindowRange = (startMs: number, endMs: number): string => {
  const start = Number.isNaN(startMs) ? UNREADABLE_DATE_SYMBOL : formatCampaignWindow(startMs)
  if (endMs === Number.POSITIVE_INFINITY) {
    return `${start} → ${NO_END_DATE_SYMBOL}`
  }

  const end = Number.isNaN(endMs) ? UNREADABLE_DATE_SYMBOL : formatCampaignWindow(endMs)

  return `${start} → ${end}`
}

/** `"2 Jul 2026, 14:30 → ∞"` — an empty end date reads as indefinite. */
export const formatWindow = (announcement: AnnouncementAdminSchema): string =>
  formatWindowRange(
    Date.parse(announcement.start_date),
    announcement.end_date === null || announcement.end_date === undefined
      ? Number.POSITIVE_INFINITY
      : Date.parse(announcement.end_date),
  )
